import { Avatar, Button, Grid, makeStyles, Theme } from "@material-ui/core";
import React, { useState } from "react";
import { RecordHeader } from "@ofqual/portal-components/components/RecordHeader";
import { ItemState } from "../../redux/state";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import {
  ScopeApplication,
  ScopeApplicationState,
} from "../../domain/ScopeApplication";
import { AssignDialog } from "./activityFeed/dialogs/AssignDialog";

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridItem: {
      borderRight: "1px solid rgba(255,255,255,0.2)",
      padding: "0 15px",
      "& div": {
        padding: "5px",
      },
    },
    gridButton: {
      color: "#f5f5f5",
    },
    avatar: {
      backgroundColor: "#293445",
      width: 70,
      height: 70,
      fontSize: 30,
    },
  };
});

type ItemHeaderProps = {
  state: ItemState;
  isAdmin: boolean;
};

const calculateInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("");
};

export const renderAssignTo = (item: ScopeApplication) => (
  <div>
    <label>
      <strong>Assigned to: </strong>
    </label>
    <br />
    <span data-test="metadata-assigned-to">
      {item.assignedToDisplayName
        ? item.assignedToDisplayName
        : item.assignedToUpn
        ? item.assignedToUpn
        : "Unassigned"}
    </span>
  </div>
);

export const ItemHeader: React.FC<ItemHeaderProps> = ({ state, isAdmin }) => {
  const { item } = state;
  const [openDialog, setOpenDialog] = useState<"AssignItem" | null>(null);

  const isApplicationSubmitted =
    item?.state === ScopeApplicationState.Submitted;
  const isApplicationDraft = item?.state === ScopeApplicationState.Draft;

  const classes = useStyles();
  return (
    <>
      <RecordHeader>
        {item ? (
          <Grid
            container
            direction="row"
            justify="flex-start"
            alignItems="stretch"
            spacing={2}
            style={{ height: "100%" }}
          >
            <Grid className={classes.gridItem}>
              <div>
                <label>
                  <strong>Created: </strong>
                </label>
                <span data-test="metadata-created-date">
                  {formatUtcDate(
                    item?.createdAt,
                    standardDateFormats.DATE_TIME
                  )}
                </span>
              </div>
              <div>
                <label>
                  <strong>Updated: </strong>
                </label>
                <span data-test="metadata-updated-date">
                  {formatUtcDate(
                    item?.updatedAt,
                    standardDateFormats.DATE_TIME
                  )}
                </span>
              </div>
              <div>
                <label>
                  <strong>Status: </strong>
                </label>
                <span data-test="metadata-status">
                  {ScopeApplicationState[item.state]}
                </span>
              </div>
            </Grid>
            {item.submittedByName ? (
              <>
                <Grid item>
                  <Avatar className={classes.avatar}>
                    {calculateInitials(item.submittedByName)}
                  </Avatar>
                </Grid>
                <Grid className={classes.gridItem}>
                  <div>
                    <label>
                      <strong>Submitted By: </strong>
                    </label>
                    <span data-test="metadata-created-date">
                      {item.submittedByName}
                    </span>
                  </div>
                  <div>
                    <label>
                      <strong>Submitted: </strong>
                    </label>
                    <span data-test="metadata-submitted-date">
                      {formatUtcDate(
                        item.submittedAt,
                        standardDateFormats.DATE_TIME
                      )}
                    </span>
                  </div>
                </Grid>
              </>
            ) : null}
            {isAdmin && isApplicationSubmitted ? (
              <Grid className={classes.gridItem}>
                <Button
                  onClick={() => setOpenDialog("AssignItem")}
                  data-test="en-assigned-to"
                  className={classes.gridButton}
                >
                  <Avatar className={classes.avatar}>
                    {item.assignedToDisplayName
                      ? calculateInitials(item.assignedToDisplayName)
                      : ""}
                  </Avatar>
                  {item.assignedToDisplayName ? (
                    renderAssignTo(item)
                  ) : (
                    <div>
                      <label>
                        <strong>Assign to</strong>
                      </label>
                    </div>
                  )}
                </Button>
              </Grid>
            ) : !isApplicationDraft ? (
              <>
                <Grid item>
                  <Avatar className={classes.avatar}>
                    {item.assignedToDisplayName
                      ? calculateInitials(item.assignedToDisplayName)
                      : ""}
                  </Avatar>
                </Grid>
                <Grid className={classes.gridItem}>{renderAssignTo(item)}</Grid>
              </>
            ) : null}
          </Grid>
        ) : null}
      </RecordHeader>
      {openDialog === "AssignItem" ? (
        <AssignDialog application={item!} onClose={() => setOpenDialog(null)} />
      ) : null}
    </>
  );
};
